import {Gender, Lang} from '@context/CompetitionImamrezaContext';
import {useScopedTranslation} from '@hooks/useScopedTranslation';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	useTheme
} from '@mui/material';
import {getSafeLangImage} from '@utils';
import {handleShare} from '@utils/share';
import {toBlob} from 'html-to-image';
import React, {useEffect, useRef, useState} from 'react';
import ReactHtmlParser from 'react-html-parser';
import {useNavigate} from 'react-router-dom';
import circleImage from '../assets/images/circle.webp';

import bg from '../assets/images/m4/bg.webp';
import gift_ar from '../assets/images/m4/gift-ar.webp';
import gift_en from '../assets/images/m4/gift-en.webp';
import gift_fa from '../assets/images/m4/gift-fa.webp';
import invite_ar from '../assets/images/m4/invite-ar.webp';
import invite_en from '../assets/images/m4/invite-en.webp';
import invite_fa from '../assets/images/m4/invite-fa.webp';

import female from '../assets/images/m4/searcher/female2.webp';
import male from '../assets/images/m4/searcher/male2.webp';
import pl_ar from '../assets/images/m4/searcher/pl-ar.webp';
import pl_en from '../assets/images/m4/searcher/pl-en.webp';

import pl_fa from '../assets/images/m4/searcher/pl-fa.webp';
import sn_ar from '../assets/images/m4/searcher/sn-ar.webp';
import sn_en from '../assets/images/m4/searcher/sn-en.webp';

import sn_fa from '../assets/images/m4/searcher/sn-fa.webp';
import sr_ar from '../assets/images/m4/searcher/sr-ar.webp';
import sr_en from '../assets/images/m4/searcher/sr-en.webp';

import sr_fa from '../assets/images/m4/searcher/sr-fa.webp';

import title_ar from '../assets/images/m4/searcher/title-ar.webp';
import title_en from '../assets/images/m4/searcher/title-en.webp';
import title_fa from '../assets/images/m4/searcher/title-fa.webp';
import share_ar from '../assets/images/m4/share-ar.webp';
import share_en from '../assets/images/m4/share-en.webp';
import share_fa from '../assets/images/m4/share-fa.webp';
import result_ar from '../assets/images/results/result-ar.webp';
import result_en from '../assets/images/results/result-en.webp';
import result_fa from '../assets/images/results/result-fa.webp';

type IProps = {
	state?: string;
	gender?: Gender;
	showDetails?: boolean;
	lang: Lang;
	referralCode: string;
};

const serviceImage: Record<Lang, string> = {en: sr_en, fa: sr_fa, ar: sr_ar};
const selfKnowledgeImage: Record<Lang, string> = {en: sn_en, fa: sn_fa, ar: sn_ar};
const pilgrimageImage: Record<Lang, string> = {en: pl_en, fa: pl_fa, ar: pl_ar};
const titleImage: Record<Lang, string> = {en: title_en, fa: title_fa, ar: title_ar};
const giftImage: Record<Lang, string> = {en: gift_en, fa: gift_fa, ar: gift_ar};
const shareImage: Record<Lang, string> = {en: share_en, fa: share_fa, ar: share_ar};
const inviteImage: Record<Lang, string> = {en: invite_en, fa: invite_fa, ar: invite_ar};
const resultImage: Record<Lang, string> = {en: result_en, fa: result_fa, ar: result_ar};

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const Searcher = (props: IProps) => {
	
	const {gender = 'male', showDetails = true, lang = 'fa', referralCode = ''} = props;
	const theme = useTheme();
	const [copied, setCopied] = useState<boolean>(false);
	const [showBackDialog, setShowBackDialog] = useState(false);
	// const [showForImageOnly, setShowForImageOnly] = useState(false);
	const {t} = useScopedTranslation();
	const navigate = useNavigate();
	const shareRef = useRef<HTMLDivElement>(null);
	
	let shareText = t('shareText');
	let title = t('titleText');
	let footer = t('footerText');
	
	useEffect(() => {
		const handlePopState = (e: PopStateEvent) => {
			e.preventDefault();
			setShowBackDialog(true);
			window.history.pushState(null, '', window.location.href);
		};
		window.history.pushState(null, '', window.location.href);
		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);
	
	const onShareClick = async () => {
		if (!shareRef.current) return;
		
		const fallbackUrl = `https://rezvan.novinrazavi.ir/competition/imamreza-result/${referralCode}`;
		
		try {
			// setShowForImageOnly(true); // Uncomment if you use it to show/hide UI
			
			await new Promise((resolve) => requestAnimationFrame(resolve)); // ensure UI updates
			
			// Wait for inner image in [data-no-share="false"] to load
			const hiddenImage = shareRef.current.querySelector('[data-no-share="false"] img') as HTMLImageElement | null;
			if (hiddenImage && !hiddenImage.complete) {
				await new Promise((resolve) => {
					hiddenImage.onload = () => resolve(true);
					hiddenImage.onerror = () => resolve(true); // prevent hanging if image fails
				});
			}
			
			const blob = await toBlob(shareRef.current, {
				cacheBust: true,
				filter: (node) => !(node instanceof HTMLElement && node.dataset.noShare === 'true')
			});
			
			// setShowForImageOnly(false); // Uncomment if used
			
			if (!blob) throw new Error('Could not generate image');
			
			const file = new File([blob], 'share.png', {type: blob.type});
			
			if (navigator.canShare?.({files: [file]})) {
				await navigator.share({
					title: title,
					text: `${t('i_am_searching')}\n\n${shareText}`,
					url: fallbackUrl,
					files: [file]
				});
				return;
			}
			
			await navigator.clipboard.writeText(fallbackUrl);
			handleShare({
				title,
				text: `${t('i_am_searching')}\n\n${shareText}\n\n${fallbackUrl}`
			});
		} catch (err) {
			console.error(err);
			// setShowForImageOnly(false); // Uncomment if used
			handleShare({
				title,
				text: `${t('i_am_searching')}\n\n${shareText}\n\n${fallbackUrl}`
			});
		}
	};
	
	const onInviteClick = () => {
		handleShare({
			title,
			text: `${t('inviteText')}\n\n${footer}`,
			url: `https://rezvan.novinrazavi.ir/competition/imamreza/${referralCode}`
		});
	};
	
	const onNewsClick = () => navigate('/network/36');
	
	return (
		<Box
			ref={shareRef}
			className="w-full flex flex-col h-full justify-start relative shareable-area"
			sx={{
				backgroundImage: `url(${bg})`,
				backgroundRepeat: 'no-repeat',
				backgroundSize: 'cover',
				position: 'relative' // Make sure it's relative so absolute children are inside
			}}
		>
			<div className={'w-10/12 mx-auto flex flex-col items-center'}>
				<div className="w-full h-fit absolute top-[5%] left-1/2 transform -translate-x-1/2 flex justify-center px-4">
					<img
						className="w-8/12 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl"
						src={getSafeLangImage(titleImage, lang)}
						alt="title"
					/>
				</div>
				
				{gender === 'male' ? (
					<img className="w-full absolute bottom-0 mx-auto" src={male} alt="male"/>
				) : (
					<img className="w-full absolute bottom-0 mx-auto" src={female} alt="female"/>
				)}
				
				{/* This is the result image that wasn't appearing */}
				{/*{*/}
				{/*	!showDetails && (*/}
				<div
					className={`w-full h-fit absolute bottom-0 mx-auto z-10`}
					data-no-share="true"
				>
					<img
						className="w-full h-fit mx-auto"
						src={circleImage}
						alt="result"
					/>
				</div>
				<div
					className={`w-full absolute z-9 ${lang === 'en' ? 'bottom-0' : 'bottom-7'}`}
					data-no-share="false"
				>
					<img
						className="w-10/12 h-fit mx-auto"
						src={getSafeLangImage(resultImage, lang)}
						alt="result"
					/>
				</div>
				{/*)*/}
				{/*}*/}
				
				{/* Bottom interactive buttons */}
				<div
					className="w-full flex flex-col absolute bottom-5 mx-auto items-center justify-center z-20"
					data-no-share="true"
				>
					{/*{showDetails && !showForImageOnly && (*/}
					{showDetails && (
						<>
							<div className="w-full flex flex-row items-center justify-around">
								<img
									className="w-[110px] cursor-pointer hover:scale-110 transition-transform duration-500 animate-drop-bounce"
									src={getSafeLangImage(pilgrimageImage, lang)}
									alt="box1"
									onClick={() =>
										navigate('/competition/imamreza-services/searcher/pilgrimage')
									}
								/>
								<img
									className="w-[110px] cursor-pointer hover:scale-110 transition-transform duration-500 animate-drop-bounce delay-100"
									src={getSafeLangImage(selfKnowledgeImage, lang)}
									alt="box2"
									onClick={() =>
										navigate('/competition/imamreza-services/searcher/selfKnowledge')
									}
								/>
								<img
									className="w-[110px] cursor-pointer hover:scale-110 transition-transform duration-500 animate-drop-bounce delay-200"
									src={getSafeLangImage(serviceImage, lang)}
									alt="box3"
									onClick={() => navigate('/competition/imamreza-services/searcher/services')}
								/>
							</div>
							<div className="w-fit flex flex-row items-center justify-center gap-3">
								<img
									className="w-10 h-auto cursor-pointer transition-transform duration-300 hover:scale-110 animate-scaleInOut"
									src={getSafeLangImage(inviteImage, lang)}
									alt="invite"
									onClick={onInviteClick}
								/>
								<img
									className="h-20 cursor-pointer mt-5 transition-transform duration-300 hover:scale-110 animate-scaleInOut"
									src={getSafeLangImage(giftImage, lang)}
									alt="gift"
									onClick={onNewsClick}
								/>
								<img
									className="w-10 h-auto cursor-pointer transition-transform duration-300 hover:scale-110 animate-scaleInOut"
									src={getSafeLangImage(shareImage, lang)}
									alt="share"
									onClick={onShareClick}
								/>
							</div>
						</>
					)}
				</div>
			</div>
			
			{/* Dialog for back confirmation */}
			<Dialog open={showBackDialog} onClose={() => setShowBackDialog(false)}>
				<Box dir={lang === 'en' ? 'ltr' : 'rtl'}>
					<DialogTitle>{t('back_dialog.title')}</DialogTitle>
					<DialogContent>
						<DialogContentText>{ReactHtmlParser(t('back_dialog.description'))}</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setShowBackDialog(false)} color="secondary">
							{t('back_dialog.confirm')}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>
		</Box>
	);
};
